<template lang="pug">
  .post-image(v-if="image", v-bind:class="{ showShadow: shadow }", ref="container", :style="{ width: `${width}px` }")
    a(v-if="path || href", :href="finalLink", :target="target")
        figure(:style="{ height: `${height}px` }")
          img(
            v-lazy="image"
            v-if="maxWidth"
            :title="title"
            :alt="alt"
            :style="{ width: `${width}px`, height: `${height}px` }"
            rel="noopener"
          )
        p(v-if="title") {{ title }}
    .span(v-else)
        figure(:style="{ height: `${height}px` }")
          img(
            v-lazy="image"
            v-if="maxWidth"
            :title="title"
            :alt="alt"
            :style="{ width: `${width}px`, height: `${height}px` }"
            rel="noopener"
          )
        p(v-if="title") {{ title }}
</template>

<script>
import { mapGetters } from 'vuex';
import { get, trim, startsWith } from '@/helpers';

export default {
    name: 'PostImage',
    props: {
        content: {
            type: Object,
            required: true
        },
        count: {
            type: Number,
            required: true
        },
        aspectRatio: {
            type: String
        },
        maxWidth: {
            type: Number
        },
        shadow:{
            type: Boolean
        }
    },
    computed: {
        title() {
            return get(this.content, 'title');
        },
        alt() {
            return get(this.content, 'sub_title') || 'Bild';
        },
        image() {
            const image = get(this.content, 'images.0');
            if (!image || !this.maxWidth) return false;

            let fit = get(image, 'pivot.fit') || 'fit=crop&crop=focalpoint&x=.5&y=.5&z=1';
            const parameters = {
                // ...get(fit.match(/x=(?<fx>[0-1]?\.?[0-9]*)/), 'groups'),
                // ...get(fit.match(/y=(?<fy>[0-1]?\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/z=(?<fz>[1-9]+\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/w=(?<w>[0-9]+\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/h=(?<h>[0-9]+\.?[0-9]*)/), 'groups')
            };
            // let { w, h, fx, fy, fz } = parameters;
            let { w, h, fz } = parameters;
            if (!fz) fz = 1;

            let width, height, aspectRatio, aspectRatioSplit
            //aspectRatioSplit = this.aspectRatio.split(':');

            width = image.width / fz;
            height = image.height / fz;
            aspectRatio = width / height;

            if (w && h) {
                aspectRatio = w / h;
                height = Math.min(this.maxWidth, width) / aspectRatio;

                fit = fit
                    .replace(/w=([0-9]+\.?[0-9]*)/, `w=${this.maxWidth}`)
                    .replace(/h=([0-9]+\.?[0-9]*)/, `h=${height}`);
            } else if (!w && !h) {
                aspectRatioSplit = this.aspectRatio.split(':');

                aspectRatio = aspectRatioSplit[0] / aspectRatioSplit[1];
                height = Math.min(this.maxWidth, width) / aspectRatio;

                fit = fit
                    .replace(/w=([0-9]+\.?[0-9]*)/, `w=${this.maxWidth}`)
                    .replace(/h=([0-9]+\.?[0-9]*)/, `h=${height}`);


                //fit += `&w=${Math.min(this.maxWidth, width)}`;
            }

            let format = '';
            if (this.webpSupport) format = '&format=webp';

            const storageRoute = trim(
                `${process.env.VUE_APP_API_HTTP}${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`,
                '/'
            );

            return {
                src: `${storageRoute}/images/${image.id}/${image.name}?${fit}${format}`,
                error: `${storageRoute}/images/${image.id}/${image.name}?${fit}&preview=greyscale`,
                loading: `${storageRoute}/images/${image.id}/${image.name}?${fit}&preview=blur`,
                width,
                height,
                aspectRatio
            };
        },
        width() {
            if (this.image.width) return Math.min(this.maxWidth, this.image.width);
            else return this.maxWidth;
        },
        height() {
            if (this.width && this.image.aspectRatio) return this.width / this.image.aspectRatio;
            else return undefined;
        },
        hyperlink() {
            return get(this.content, 'hyperlink') || {};
        },
        href() {
            let href = false;

            if (this.hyperlink.type !== 'internal' && get(this.hyperlink, 'href')) {
                const link = this.hyperlink.href;
                switch (this.hyperlink.type) {
                    case 'external':
                        if (!startsWith(link, 'https://') && !startsWith(link, 'http://')) href = `https://${link}`;
                        else href = link;
                        break;

                    case 'mailto':
                        href = `mailto:${link}`;
                        break;

                    case 'tel':
                        href = `tel:${link}`;
                        break;
                }
            }

            return href;
        },
        target() {
            return get(this.hyperlink, 'target') || '_self';
        },
        path() {
            if (this.hyperlink.type === 'internal' && get(this.hyperlink, 'menuItem.path'))
                return this.hyperlink.menuItem.path;
            else return false;
        },
        finalLink() {
            if (this.path) {
                return this.path;
            } else if (this.href) {
                return this.href;
            } else {
                return false;
            }
        },
        ...mapGetters({
            webpSupport: 'webpSupport'
        })
    }
};
</script>

<style lang="sass" nolang="scss" scoped>
@import "~@/assets/sass/variables"

.content .post-image
  img
    @apply w-full h-auto inline-block
    object-fit: cover
  p
    @apply mb-0 text-xs
  figure
    @apply block
    background: $color-preload-bg
    margin-bottom: 2px

.showShadow
    figure
        background: $color-preload-bg
        img
            @apply shadow-md
</style>
